import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const notificationsAdapter = createEntityAdapter({
    selectId: (notification) => notification._id,
    sortComparer: (a, b) => b.createdAt?.localeCompare(a.createdAt) // Sort by creation date, newest first
});

// Async thunk for fetching notifications
export const fetchNotifications = createAsyncThunk(
    "notification/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/notifications",
                method: "GET"
            });

            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response.message || "Failed to fetch notifications.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while fetching notifications.");
        }
    }
);

// Async thunk for marking notification as read
export const markNotificationAsRead = createAsyncThunk(
    "notification/markAsRead",
    async (_id, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/notifications/markAsRead",
                method: "POST",
                params: { _id }
            });

            if (!response.success) {
                return rejectWithValue(response.message || "Failed to mark notification as read.");
            }

            return _id;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while marking notification as read.");
        }
    }
);

// Adapter's initial state with additional fields
const initialState = notificationsAdapter.getInitialState({
    loading: false,
    error: null,
    unreadCount: 0
});

const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        clearNotifications: (state) => {
            notificationsAdapter.removeAll(state);
            state.unreadCount = 0;
            state.loading = false;
            state.error = null;
        },

        addNotificationIO: (state, action) => {
            notificationsAdapter.addOne(state, action.payload);
            if (!action.payload.read) {
                state.unreadCount += 1;
            }
        },
        addAllNotifications: (state, action) => {
            notificationsAdapter.setAll(state, action.payload);
            //state.unreadCount = action.payload.filter(notification => !notification.read).length;
        },

        incrementUnreadCount: (state) => {
            state.unreadCount += 1;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Notifications
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;
                notificationsAdapter.setAll(state, action.payload);
                //state.unreadCount = action.payload.filter(notification => !notification.read).length;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Mark Notification as Read
            .addCase(markNotificationAsRead.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
                state.loading = false;
                notificationsAdapter.updateOne(state, {
                    id: action.payload,
                    changes: { read: true }
                });
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            })
            .addCase(markNotificationAsRead.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// Export the generated entity adapter selectors
export const {
    selectAll: selectAllNotifications,
    selectById: selectNotificationById,
    selectIds: selectNotificationIds
} = notificationsAdapter.getSelectors((state) => state.notification);

// Additional selectors
export const selectUnreadNotifications = createSelector(
    selectAllNotifications,
    (notifications) => notifications.filter(notification => !notification.read)
);

export const { clearNotifications, incrementUnreadCount, addNotificationIO, addAllNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;